import { useCallback, useEffect, useState } from "react";
import { Download, FileSpreadsheet, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { money, todayET } from "@/lib/format";
import { PageHeader } from "@/components/PageHeader";
import { CollectionsChart, MonthlyTable, SummaryCards } from "@/components/reports/ReportParts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

const PRESETS = [["this_month", "This month"], ["last_month", "Last month"], ["ytd", "Year to date"], ["last_12_months", "Last 12 months"], ["custom", "Custom"]];

const download = async (path, params, setBusy) => {
  setBusy(path);
  try {
    const r = await api.get(path, { params, responseType: "blob" });
    const name = /filename="([^"]+)"/.exec(r.headers["content-disposition"] || "")?.[1] || path.split("/").pop();
    const url = URL.createObjectURL(r.data);
    const a = Object.assign(document.createElement("a"), { href: url, download: name });
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch (e) {
    toast.error(apiError(e).message || "Export failed");
  } finally {
    setBusy("");
  }
};

export default function Reports() {
  const [preset, setPreset] = useState("last_12_months");
  const [custom, setCustom] = useState({ start: `${todayET().slice(0, 4)}-01-01`, end: todayET() });
  const [params, setParams] = useState({ preset: "last_12_months" });
  const [rep, setRep] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState("");

  useEffect(() => {
    setRep(null);
    api.get("/reports/portfolio", { params }).then((r) => setRep(r.data)).catch((e) => setErr(apiError(e).message));
  }, [params]);

  const pick = (p) => {
    setPreset(p);
    setErr("");
    if (p !== "custom") setParams({ preset: p });
  };
  const applyCustom = useCallback((e) => {
    e.preventDefault();
    if (!custom.start || !custom.end) return setErr("Enter a start and end date");
    if (custom.end < custom.start) return setErr("End date must be after start date");
    setErr("");
    setParams({ preset: "custom", ...custom });
  }, [custom]);

  const exp = (path, label, Icon, testid) => (
    <Button variant="outline" onClick={() => download(path, params, setBusy)} disabled={!!busy || !rep} data-testid={testid}>
      {busy === path ? <Loader2 className="animate-spin" /> : <Icon />} {label}
    </Button>
  );

  return (
    <div data-testid="reports-page">
      <PageHeader eyebrow="Portfolio" title="Reports" subtitle="Collections, interest and fees from the ledger — voided payments excluded."
        actions={<div className="flex flex-wrap gap-2">
          {exp("/reports/portfolio.xlsx", "Export Excel", FileSpreadsheet, "export-excel-button")}
          {exp("/reports/monthly.csv", "Export CSV", Download, "export-csv-button")}
          {exp("/reports/loans.csv", "Export loans CSV", Download, "export-loans-csv-button")}
        </div>} />
      <div className="mb-6 flex flex-wrap items-end gap-3">
        <div className="inline-flex flex-wrap rounded-md border bg-card p-1" role="tablist" data-testid="report-presets">
          {PRESETS.map(([k, l]) => (
            <button key={k} onClick={() => pick(k)} data-testid={`preset-${k}`} role="tab" aria-selected={preset === k}
              className={cn("rounded px-3 py-1.5 text-sm font-medium transition-colors", preset === k ? "bg-primary text-primary-foreground" : "hover:bg-muted")}>{l}</button>
          ))}
        </div>
        {preset === "custom" && (
          <form onSubmit={applyCustom} noValidate className="flex flex-wrap items-end gap-2" data-testid="custom-range-form">
            <label className="text-xs font-semibold">From<Input type="date" value={custom.start} onChange={(e) => setCustom({ ...custom, start: e.target.value })} className="mt-1 h-9" data-testid="custom-start-input" /></label>
            <label className="text-xs font-semibold">To<Input type="date" value={custom.end} onChange={(e) => setCustom({ ...custom, end: e.target.value })} className="mt-1 h-9" data-testid="custom-end-input" /></label>
            <Button type="submit" size="sm" className="h-9" data-testid="custom-apply-button">Apply</Button>
          </form>
        )}
      </div>
      {err && <p className="mb-4 text-sm font-medium text-destructive" data-testid="report-error">{err}</p>}
      {!rep && !err && <div className="py-16 text-center"><Loader2 className="mx-auto animate-spin" /></div>}
      {rep && (
        <>
          <div className="mb-4 text-sm text-muted-foreground" data-testid="report-range">{rep.start} to {rep.end} · current figures as of {rep.as_of} · total collected <b className="num text-foreground">{money(rep.summary.collected_cents)}</b></div>
          <SummaryCards s={rep.summary} />
          <CollectionsChart months={rep.months} />
          <MonthlyTable rep={rep} />
        </>
      )}
    </div>
  );
}
